import { type ReactNode } from 'react';
import { MenuButton, MenuItem } from '@carbon/react';
import { useAtom } from 'jotai';
import routes from '~/routes';
import { useEval3Page } from './context';
import { AriesMdbExportModal } from 'components/aries-mdb-export';
import { useAriesMdbExportFlow } from './use-aries-mdb-export-flow';
import { ExportContentModal } from './export-content-modal';
import { useContentExportFlow } from './content-export';
import { useScenarioAssumptionsExportFlow } from './use-scenario-assumptions-export-flow';
import {
  type WorkingConfigForm,
  useCalcLifecycle,
  useShownDcaScenarioId,
} from './hooks';
import { createDcaWorkspaceAtom } from 'components/dca-workspace/atoms';
import {
  buildCompareWizardUrl,
  EVAL3_SOURCE,
} from '../../compare/wizard/utils';

/**
 * The center-header "Actions" dropdown (mock §1). A thin container that owns the
 * labeled "Actions" `MenuButton` and renders one menu item per scoped action;
 * each action's data flow + any modal is encapsulated in its own hook, so adding
 * a future action (Clone / Carried Interest) is a new
 * `MenuItem` + hook here rather than a change to any one action's internals.
 *
 * Always shown: **Export Content** works in every view (eval + package/workspace),
 * so the menu is always available. **Export Aries MDB** and **Export Scenario
 * Assumptions** are per-calculation, so their items show only on an eval route (a calc
 * selected). Every action hook is called unconditionally (rules of hooks); the per-calc
 * ones no-op / are unreachable when no calc is selected.
 *
 * On narrow screens the responsive header (`HeaderActions`) passes the otherwise-standalone
 * Eval Settings + Save controls in as `children` so they fold into this menu (Carbon renders
 * MenuButton children as-is; `MenuItem`s register via menu context, so wrapper components
 * that return a `MenuItem` work fine).
 *
 * @param {object} props - The working-config form, and optional extra menu items (`children`)
 *   folded in on narrow screens.
 * @return {JSX.Element} The Actions menu + its modals + the content-export toast.
 */
export function ActionsMenu({
  workingConfig,
  children,
}: {
  /** The eval settings form, for the DCA scenario a new DCA workspace opens on. */
  workingConfig: WorkingConfigForm;
  children?: ReactNode;
}): JSX.Element {
  const { packageId, calculationId } = useEval3Page();
  const ariesExport = useAriesMdbExportFlow();
  const contentExport = useContentExportFlow();
  const scenarioAssumptionsExport = useScenarioAssumptionsExportFlow(
    calculationId,
    {
      // Offset this toast below the content-export toast only while that one is actually showing,
      // so two concurrent export toasts don't overlap (and a lone toast doesn't float with a gap).
      // `toast` is `null | element` (never undefined), so a strict null check is correct here.
      stackBelow: contentExport.toast !== null,
    }
  );
  // Disable the per-calc exports only when the calc is known NOT ready — mid-run or errored.
  // NOT during the detail-load window (`resolving`): greying out a just-selected calc with no
  // reason (as a bare `calcDetail?.status === 'Completed'` check would, since data is undefined
  // while the request is in flight) was the regression to fix. useCalcLifecycle distinguishes
  // resolving from processing/error where a status check alone cannot.
  const { state: calcState } = useCalcLifecycle(calculationId);
  const exportDisabled = calcState === 'processing' || calcState === 'error';
  const [dcaState, dcaMutate] = useAtom(createDcaWorkspaceAtom);
  const dcaScenarioId = useShownDcaScenarioId(workingConfig);

  // Pre-fill the Compare wizard's Origin with the open eval (EVAL3_SOURCE =
  // EvaluationCalculation, whose id is calculationId). In package/workspace mode
  // (no calc selected) there's nothing to pre-fill, so open the wizard empty.
  const compareUrl =
    calculationId !== null
      ? buildCompareWizardUrl(calculationId, EVAL3_SOURCE)
      : routes.compareWizardPath;

  const handleCreateDcaWorkspace = () => {
    dcaMutate({
      type: 'POST',
      url: '/dca_workspaces',
      data: { parentType: 'Package', parentId: packageId, dcaScenarioId },
      onSuccess: (data) => {
        window.open(data.href, '_blank', 'noopener,noreferrer');
      },
    });
  };

  return (
    <>
      <MenuButton
        label="Actions"
        kind="tertiary"
        size="md"
        // Anchor the menu to the button's right edge (it sits at the right end
        // of the header) so it expands leftward and never clips off-screen.
        menuAlignment="bottom-end"
      >
        {/* Disabled while an export is in flight so a second one can't be kicked
            off (matches the old standalone Export button's disabled state). */}
        <MenuItem
          label="Export Content"
          disabled={contentExport.isExporting}
          onClick={contentExport.open}
        />
        {calculationId !== null && (
          <MenuItem
            label="Export Aries MDB"
            disabled={exportDisabled}
            onClick={ariesExport.open}
          />
        )}
        {/* Per-calculation (like Export Aries MDB): the scenario-assumptions XLSX is
            keyed to a selected eval. Reuses the legacy AFCF export endpoint; the
            workbook resolves scenarios from resolved_scenario_ids so it works for
            case-mode evals too. */}
        {calculationId !== null && (
          <MenuItem
            label="Export Scenario Assumptions"
            disabled={exportDisabled || scenarioAssumptionsExport.isExporting}
            onClick={scenarioAssumptionsExport.start}
          />
        )}
        <MenuItem
          label="Create DCA Workspace"
          disabled={dcaState.loading}
          onClick={handleCreateDcaWorkspace}
        />
        {/* Opens the package-compare wizard in a new tab (same entry point as the
            packages list's "Compare" action) — a cross-package tool, so it's always
            available rather than gated on a selected calculation. When a calc is open
            it pre-fills the wizard's Origin with this eval; otherwise it opens empty. */}
        <MenuItem
          label="Package Compare"
          onClick={() =>
            window.open(compareUrl, '_blank', 'noopener,noreferrer')
          }
        />
        {/* Collapsed-mode actions (Eval Settings / Save) folded in on narrow screens. */}
        {children}
      </MenuButton>
      <ExportContentModal {...contentExport.modalProps} />
      <AriesMdbExportModal {...ariesExport.modalProps} />
      {contentExport.toast}
      {scenarioAssumptionsExport.toast}
    </>
  );
}
